import { ChangeDetectorRef, Component, Inject, OnInit } from "@angular/core";
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import {
  FormExaminationFaultDto,
  FormRectificationWorkDto,
  LoadCellPhotoResponse,
  QuoteInformationDto,
} from "app/api/models";
import {
  EmailService,
  EmployeeService,
  Form10AService,
  LoadCellPhotoService,
} from "app/api/services";
import { AppLoaderService } from "app/core/services/app-loader/app-loader.service";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { PrintQuotationEmailDialogComponent } from "./print-quotation-email-dialog/print-quotation-email-dialog.component";
import { CommonModule, CurrencyPipe, DatePipe } from "@angular/common";
import { FlexLayoutModule } from "@angular/flex-layout";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatListModule } from "@angular/material/list";
import { MatMenuModule } from "@angular/material/menu";
import { MatRadioModule } from "@angular/material/radio";
import { MatSelectModule } from "@angular/material/select";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatTooltipModule } from "@angular/material/tooltip";
import { DLMSharedModule } from "app/views/calibrations/shared/shared.module";
import { DragScrollModule } from "ngx-drag-scroll";
import { NgxPrintModule } from "ngx-print";
import { PdfComponent } from "app/core/components/pdf/pdf.component";
import autoTable, { Styles, UserOptions } from "jspdf-autotable";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { getUniqueOnProp, getUniqueOnSelf } from "app/app.component";

@Component({
  selector: "app-print-quotation-dialog",
  templateUrl: "./print-quotation-dialog.component.html",
  styleUrls: ["./print-quotation-dialog.component.scss", "./../../../../_styles/calibration-overrides.scss"],
  standalone: true,
  imports: [
    CommonModule,
    DLMSharedModule,
    MatListModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatInputModule,
    MatMenuModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    FlexLayoutModule,
    DragScrollModule,
    NgxPrintModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatRadioModule,
  ]
})
export class PrintQuotationDialogComponent extends PdfComponent implements OnInit {
  model: QuoteInformationDto;
  urls: SafeUrl[] = [];
  loadCellPhotos: LoadCellPhotoResponse[] = [];

  currentUser: string;
  
  public GetQuotationTitle = () => `Quotation-R${this.model?.quoteId}.pdf`;

  constructor(
    public dialogRef: MatDialogRef<PrintQuotationDialogComponent>,
    private emailService: EmailService,
    private loader: AppLoaderService,
    private snack: MatSnackBar,
    private dialog: MatDialog,
    private employeeService: EmployeeService,
    private form10aService: Form10AService,
    private datePipe: DatePipe,
    private currencyPipe: CurrencyPipe,
    private loadCellPhotoService: LoadCellPhotoService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    super();
  }

  ngOnInit(): void {
    if (!this.data) return;

    this.employeeService.apiEmployeeEmployeeGet$Json().subscribe((res) => {
      if (!res) return;

      this.currentUser = res.data?.firstName + " " + res.data?.lastName;
    });

    this.form10aService
      .apiForm10AQuoteInformationPost$Json({ quoteId: this.data.quoteId })
      .subscribe((res) => {
        if (!res) return;
        this.model = res?.data;

        this.populatePhotos();
      });
  }

  getFaultDescription(fault: FormExaminationFaultDto) {
    // return fault?.comment ?? '';

    var name = fault?.examinationFaultOption?.name;
    var comment = fault?.comment;

    if(name == 'Other')
      name = '';

    if (!name && !comment) return '';

    return [name, comment].filter(x => x && x.trim() != '').join(' - ');
  }

  getRectificationWork(work: FormRectificationWorkDto) {
    // return work?.comment ?? '';

    var name = work?.rectificationWorkOption?.name;
    var comment = work?.comment;

    if(name == 'Other')
      name = '';

    if (!name && !comment) return '';

    return [name, comment].filter(x => x && x.trim() != '').join(' - ');
  }

  groupRectificationWorks() {
    if (!this.model) return [];

    var newWorks: FormRectificationWorkDto[] = [];

    for (var work of this.model?.rectificationWorkRequired) {
      var index = newWorks.findIndex(x => x.serialNumber == work.serialNumber);

      if (index == -1)
        newWorks.push({
          serialNumber: work.serialNumber,
          comment: work.comment ?? '',
          rectificationWorkOption: Object.assign(
            {},
            work.rectificationWorkOption
          ),
        });
      else newWorks[index].comment += "\n" + this.getRectificationWork(work);
    }

    return newWorks;
  }

  groupFaultsFound() {
    if (!this.model) return [];

    var newFaults: FormExaminationFaultDto[] = [];

    for (var fault of this.model?.faultsFound) {
      var index = newFaults.findIndex(
        (x) => x.serialNumber == fault.serialNumber
      );

      if (index == -1)
        newFaults.push({
          serialNumber: fault.serialNumber,
          comment: fault.comment,
          examinationFaultOption: Object.assign(
            {},
            fault.examinationFaultOption
          ),
        });
      else newFaults[index].comment += "\n" + this.getFaultDescription(fault);
    }

    return newFaults;
  }

  getTotalPartsCost() {
    return this.model?.formParts?.reduce(
      (sum, part) =>
        sum +
        (part?.partOption?.number == "3rd Party"
          ? part?.cost
          : part?.partOption?.cost) *
          (part?.partOption?.markup + 1) *
          part?.quantity,
      0
    );
  }

  showPressureTestCost() {
    return (
      this.model?.pressureTestCost ||
      this.groupRectificationWorks()?.filter((x) =>
        this.getRectificationWork(x)?.toLowerCase().includes("pressure test")
      ).length > 0
    );
  }

  getIncludedImages() {
    return this.urls?.filter((_, i) => this.imageIsIncluded(i)) ?? [];
  }

  imageIsIncluded(index: number) {
    return this.loadCellPhotos[index].showOnQuote;
  }

  toggleIncludeImage(index: number) {
    this.loadCellPhotos[index].showOnQuote = !this.loadCellPhotos[index].showOnQuote;

    //save it
    this.loadCellPhotoService.apiLoadCellPhotoUpdateImageInfoPost$Json$Response({body: this.loadCellPhotos[index]}).subscribe();
  }

  populatePhotos() {
      if (!this.model.quoteId)
        return;
      
      var blobUrl: SafeUrl;
      // this.files = [];
      this.urls = [];

      //todo MULTIPLE FORM10as
  
      // this.loaded = false;
      this.loadCellPhotoService
        .apiLoadCellPhotoForm10APhotosGet$Json({ form10aId: this.model.form10aIds[0] })
        .subscribe((res) => {
          this.loadCellPhotos = res;
          res.forEach((loadcell) => {
            this.loadCellPhotoService
              .apiLoadCellPhotoGetImageLoadCellPhotoIdGet$Response({ loadCellPhotoId: loadcell.id })
              .subscribe((res) => {
                var blob = new Blob([res.body]);
                var file = new File([blob], loadcell.name);
  
                blobUrl = this.sanitizer.bypassSecurityTrustUrl(
                  URL.createObjectURL(blob)
                );
                // this.files.push(file);
                this.urls.push(blobUrl);
              });
          });
  
          // this.loaded = true;
          this.cdr.detectChanges();
        });
    }

    getEquipmentList() {
      var allEquip = this.model?.serialNumbers?.map((_, i) => `${this.model.workingLoadLimits[i]} ${this.model.loadCellTypes[i]}`) ?? [];

      var totalEquip: {quantity: number, item: string}[] = [];

      for(var equip of allEquip) {
        var existing = totalEquip.find(x => x.item == equip);

        if(existing)
          existing.quantity++;
        else
          totalEquip.push({quantity: 1, item: equip});
      }

      return totalEquip.map(x => x.quantity == 1 ? x.item : `${x.quantity}x ${x.item}`);
    }
  
  private async preparePdf(callback: (pdfDoc: jsPDF) => any) {
    let PDF = new jsPDF("p", "mm", "a4", true);

    this.loader.open();

    //title
    this.addTextToPdf(PDF, [[`QUOTATION R${this.model?.quoteId}`]], 16, null, 50, null, true);

    var cellStyle: Partial<Styles> = { lineColor: "#000000", textColor: "#000000", fontSize: 9, lineWidth: 0.2, cellPadding: {vertical: 1, horizontal: 2}, fontStyle: 'normal', };
    var greyCell: Partial<Styles> = { ...cellStyle, fillColor: "#e6e6e6", cellWidth: 40, };
    var whiteCell: Partial<Styles> = { ...cellStyle, fillColor: "#FFFFFF", cellWidth: 'auto', };

    var noRightCell: Partial<Styles> = { ...whiteCell, lineWidth: {right: 0, left: 0.2, top: 0.2, bottom: 0.2} };
    var noLeftCell: Partial<Styles> = { ...whiteCell, lineWidth: {right: 0.2, left: 0, top: 0.2, bottom: 0.2} };
    // var noHorizCell: Partial<Styles> = { ...whiteCell, lineWidth: {right: 0, left: 0, top: 0.2, bottom: 0.2} };
    var noTopCell: Partial<Styles> = { ...whiteCell, lineWidth: {right: 0.2, left: 0.2, top: 0, bottom: 0.2} };
    var noBottomCell: Partial<Styles> = { ...whiteCell, lineWidth: {right: 0.2, left: 0.2, top: 0.2, bottom: 0} };

    var tlCell: Partial<Styles> = { ...whiteCell, lineWidth: {right: 0, left: 0.2, top: 0.2, bottom: 0} };
    var trCell: Partial<Styles> = { ...whiteCell, lineWidth: {right: 0.2, left: 0, top: 0.2, bottom: 0} };
    var blCell: Partial<Styles> = { ...whiteCell, lineWidth: {right: 0, left: 0.2, top: 0, bottom: 0.2} };
    var brCell: Partial<Styles> = { ...whiteCell, lineWidth: {right: 0.2, left: 0, top: 0, bottom: 0.2} };
    var tCell: Partial<Styles> = { ...whiteCell, lineWidth: {right: 0, left: 0, top: 0.2, bottom: 0} };
    var lCell: Partial<Styles> = { ...whiteCell, lineWidth: {right: 0, left: 0.2, top: 0, bottom: 0} };
    var rCell: Partial<Styles> = { ...whiteCell, lineWidth: {right: 0.2, left: 0, top: 0, bottom: 0} };
    var bCell: Partial<Styles> = { ...whiteCell, lineWidth: {right: 0, left: 0, top: 0, bottom: 0.2} };
    var xCell: Partial<Styles> = { ...whiteCell, lineWidth: 0 };

    var autoTableOptions: UserOptions = {theme: "grid", body: [], tableWidth: 'auto', margin: {horizontal: 16, top: 50, bottom: 30}, /*columnStyles: { 0: { cellWidth: 32 }, 1: { cellWidth: 64 }, 2: { cellWidth: 32 }, 3: { cellWidth: 64 }},*/ };


    //table 1
    autoTable(PDF, {
      ...autoTableOptions,
      margin: {horizontal: 46},
      startY: ((PDF as any).lastAutoTable.finalY || 15) + 4,
      head: [
          [
            { content: "Customer", styles: {...greyCell, fontStyle: 'bold'} },
            { content: this.model.customerName ?? '', styles: {...whiteCell, fontStyle: 'bold'} },
          ],
          [
            { content: "Contact", styles: {...greyCell, fontStyle: 'bold'} },
            { content: this.model.quoteContactName ?? '', styles: {...whiteCell, fontStyle: 'bold'} },
          ],
          [
            { content: "Email Address", styles: {...greyCell, fontStyle: 'bold'} },
            { content: this.model.quoteContactEmail ?? '', styles: {...whiteCell, fontStyle: 'bold'} },
          ],
          [
            { content: "Date", styles: {...greyCell, fontStyle: 'bold'} },
            { content: this.datePipe.transform(this.model.date ?? '', 'dd/MM/yyyy'), styles: {...whiteCell, fontStyle: 'bold'} },
          ],
          [
            { content: "Sales Order No", styles: {...greyCell, fontStyle: 'bold'} },
            { content: this.model?.salesOrders?.join('\n') ?? '', styles: {...whiteCell, fontStyle: 'bold'} },
          ],
          [
            { content: "Equipment Submitted", styles: {...greyCell, fontStyle: 'bold'} },
            { content: /*`${this.model?.serialNumbers?.length > 1 ? this.model?.serialNumbers.length + "x" : ""}` +
                        `${this.model?.workingLoadLimit ?? '' } ${this.model?.loadCellType ?? ''}`,*/
                // this.model?.serialNumbers?.map((_, i) => `${this.model.workingLoadLimits[i]} ${this.model.loadCellTypes[i]}`).join('\n') ?? '',
                this.getEquipmentList()?.join('\n') ?? '',
                styles: {...whiteCell, fontStyle: 'bold'} },
          ],
          [
            { content: "Serial Number", styles: {...greyCell, fontStyle: 'bold'} },
            { content: this.model.serialNumbers.join('\n') ?? '', styles: {...whiteCell, fontStyle: 'bold'} },
          ],
          [
            { content: "Purchase Order No", styles: {...greyCell, fontStyle: 'bold'} },
            { content: this.model?.purchaseOrders?.join('\n') ?? '', styles: {...whiteCell, fontStyle: 'bold'} },
          ],
          [
            { content: "DLM Contact", styles: {...greyCell, fontStyle: 'bold'} },
            { content: this.currentUser ?? '', styles: {...whiteCell, fontStyle: 'bold'} },
          ],
      ],
    });

    //text
    var prelimText = 'Thank you for considering DLM. Our preliminary inspection of your equipment has resulted in the report set out below. We must emphasise that our investigation so far has only been a visual inspection and a bench test, the remedial action detailed below will serve only to enable us to proceed with further, dynamic tests. This later stage of testing may reveal further faults with the equipment which may necessitate further rectification at additional cost.\nThe authorisation now sought from you is for us to proceed with the initial rectification outlined below, at the cost shown. We would require written an order authorising the incurring of the costs detailed before proceeding to the second stage of investigation. Please be aware that we are unable to accept verbal authorisation alone to proceed with work. Should any further fault be found after this stage we shall re-quote and await an amended order before proceeding with the work.';
    this.addTextToPdf(PDF, [[prelimText]], 8, 0, ((PDF as any).lastAutoTable.finalY || 15) + 1, null, null, {horizontal: 20, vertical: 8}, true, true);
    
    //fault table
    autoTable(PDF, {
      ...autoTableOptions,
      startY: ((PDF as any).lastAutoTable.finalY || 15) + 4,
      head: [
          [
            { content: "Fault Conditions revealed by preliminary test:\n", styles: {...noBottomCell, fontStyle: 'bold'}, colSpan: 2 },
          ],
          ...this.groupFaultsFound().map(x => [
            { content: x.serialNumber + ":", styles: <any>{ ...lCell, cellWidth: 32, fontStyle: 'bold' } },
            { content: this.getFaultDescription(x), styles: rCell },
          ]),
          [
            { content: '', styles: noTopCell, colSpan: 2 },
          ],
          
          [
            { content: "Rectification Work required before second stage testing:\n", styles: {...noBottomCell, fontStyle: 'bold'}, colSpan: 2 },
          ],
          ...this.groupRectificationWorks().map(x => [
            { content: x.serialNumber + ":", styles: { ...lCell, cellWidth: 32, fontStyle: 'bold' } },
            { content: this.getRectificationWork(x), styles: rCell },
          ]),

          [
            { content: '', styles: noTopCell, colSpan: 2 },
          ],
      ],
    });

    PDF.addPage();

    //parts table left
    autoTable(PDF, {
      ...autoTableOptions,
      startY: 56,
      tableWidth: 89,
      head: [
          [
            { content: 'Parts:', styles: {...noBottomCell, fontStyle: 'bold'}, colSpan: 4, },
          ],
          [
            { content: "Description", styles: {...lCell, fontStyle: 'bold'} },
            { content: "Qty", styles: {...xCell, fontStyle: 'bold'} },
            { content: "Price", styles: {...xCell, fontStyle: 'bold'} },
            { content: "Total", styles: {...rCell, fontStyle: 'bold'} },
          ],
          ...this.model?.formParts.map(x => [
            { content: x.partOption?.description ?? '', styles: lCell },
            { content: x.quantity ?? '', styles: xCell },
            { content: this.currencyPipe.transform((x?.partOption?.number == "3rd Party" ? x?.cost : x?.partOption?.cost) * (x?.partOption?.markup + 1), 'GBP'), styles: xCell },
            { content: this.currencyPipe.transform(((x?.partOption?.number == "3rd Party" ? x?.cost : x?.partOption?.cost) * (x?.partOption?.markup + 1) * x?.quantity), 'GBP'), styles: xCell },
          ]),
          [
            { content: '\nParts Cost', styles: {...lCell, fontStyle: 'bold'}, colSpan: 3, },
            { content: '\n' + this.currencyPipe.transform(this.getTotalPartsCost(), 'GBP'), styles: rCell, },
          ],
          [
            { content: '\nIf you wish to proceed with the advisory repair then please add these costs to your purchase order and advise us accordingly.', styles: noTopCell, colSpan: 4, },
          ],
      ],
    });

    var leftBottomY = (PDF as any).lastAutoTable.finalY ?? 0;

    //parts table right
    autoTable(PDF, {
      ...autoTableOptions,
      startY: 56,
      margin: {left: 105},
      tableWidth: 89,
      head: [
          [
            { content: 'Cost of preliminary work detailed above:', styles: {...noBottomCell, fontStyle: 'bold'}, colSpan: 4, },
          ],
          [
            { content: "", styles: lCell },
            { content: "Qty", styles: {...xCell, fontStyle: 'bold'} },
            { content: "Cost", styles: {...xCell, fontStyle: 'bold'} },
            { content: "Total", styles: {...rCell, fontStyle: 'bold'} },
          ],
          [
            { content: "Labour", styles: {...lCell, fontStyle: 'bold'} },
            { content: "", styles: xCell },
            { content: "", styles: xCell },
            { content: this.currencyPipe.transform(this.model?.labourHours ?? 0, 'GBP'), styles: rCell },
          ],
          [
            { content: "Parts", styles: {...lCell, fontStyle: 'bold'} },
            { content: "", styles: xCell },
            { content: "", styles: xCell },
            { content: this.currencyPipe.transform(this.getTotalPartsCost() ?? 0, 'GBP'), styles: rCell },
          ],
          [
            { content: "Calibration", styles: {...lCell, fontStyle: 'bold'} },
            { content: this.model?.calibrationFeeBreakdown?.length || 1, styles: xCell },
            { content: this.currencyPipe.transform((this.model?.calibrationFee ?? 0) / (this.model?.calibrationFeeBreakdown?.length || 1), 'GBP'), styles: xCell },
            { content: this.currencyPipe.transform(this.model?.calibrationFee ?? 0, 'GBP'), styles: rCell },
          ],

          // ...this.model?.calibrationFeeBreakdown.map(x => [
          //   { content: 'Calibration ' + (x.description ?? ''), styles: <any>{...lCell, fontStyle: 'italic'} },
          //   { content: '', styles: xCell },
          //   { content: '', styles: xCell },
          //   { content: '' /*this.currencyPipe.transform(x?.calibrationFee ?? 0, 'GBP')*/, styles: rCell },
          // ]),
          
          [
            { content: "Carriage", styles: {...lCell, fontStyle: 'bold'} },
            { content: "", styles: xCell },
            { content: "", styles: xCell },
            { content: this.currencyPipe.transform(this.model?.carriage ?? 0, 'GBP'), styles: rCell },
          ],

          ...(this.showPressureTestCost() ? [[
            { content: "Pressure Test Cost", styles: <any>{...lCell, fontStyle: 'bold'} },
            { content: "", styles: xCell },
            { content: "", styles: xCell },
            { content: this.currencyPipe.transform(this.model?.pressureTestCost ?? 0, 'GBP'), styles: rCell },
          ]] : []),
          
          [
            { content: "\nTotal", styles: {...blCell, fontStyle: 'bold'} },
            { content: "", styles: bCell },
            { content: "", styles: bCell },
            { content: '\n' + this.currencyPipe.transform(this.model?.total ?? 0, 'GBP'), styles: brCell },
          ],
          
      ],
    });

    var rightBottomY = (PDF as any).lastAutoTable.finalY ?? 0;

    var partsBottomY = Math.max(leftBottomY, rightBottomY);

    var nbText = 'NB: The figures shown for these costs do not include VAT, which will be added where applicable.\nIn the event that this quote is unsuccessful for any of the equipment listed, inspection fee per item and carriage charges are applicable.';
    this.addTextToPdf(PDF, [[nbText]], 8, 0, (partsBottomY || 15) + 1, null, true, {horizontal: 32, vertical: 8}, true, true);
    
    
    //images if applicable
    var images = this.getIncludedImages();
    if(images?.length) {
      
      this.addTextToPdf(PDF, [['Images of your Submitted Equipment']], 12, 0, ((PDF as any).lastAutoTable.finalY || 15) + 4, null, null, {horizontal: 20, vertical: 8}, true, true);
      this.addTextToPdf(PDF, [['The images below show the loadcell received.\nThis photo serves as a reference for the item assessed under this quotation.']], 8, 0, ((PDF as any).lastAutoTable.finalY || 15) + 0, null, null, {horizontal: 20, vertical: 8}, true, true);
      
      await this.addImages(PDF);
    }

    PDF.addPage();

    this.addTextToPdf(PDF, [['CUSTOMER AUTHORISATION']], 16, null, 50, null, true, null, true, true);

    var authText = '\nWe authorise you to continue with the preliminary rectification work described and we accept the costs shown, together with carriage charges and VAT as applicable, in respect of which our order number is detailed below.' +
    '\n\n\nCustomer:' +
    '\n\nSales Order Number:' +
    '\n\nQuote Number:' +
    '\n\nOrder Number: ………………………………………………………………………' +
    '\n\nDelivery Address: ………………………………………………………………………' +
    '\n\n\n' +
    '\n\nPrint Name: ………………………………………………………………………' +
    '\n\nSigned: ………………………………………………………………………' +
    '\n\nDate: ………………………………………………………………………';
    this.addTextToPdf(PDF, [[authText]], 9, 0, ((PDF as any).lastAutoTable.finalY || 15) + 1, null, null, {horizontal: 20, vertical: 8}, true, true);

    this.addTextToPdf(PDF, [['\n\nSubject to DLM\'s standard terms and conditions']], 8, 0, ((PDF as any).lastAutoTable.finalY || 15) + 1, null, null, {horizontal: 20, vertical: 8}, true, true);

    this.addHeaderAndFooter(PDF).then(PDF => {
      this.loader.close();
      callback(PDF);
    });
  }
  
  async addHeaderAndFooter(PDF: jsPDF) {
    for(var i=0; i < PDF.getNumberOfPages(); i++) {
      PDF.setPage(i);
      var canvas = await this.createCanvasFromElement("print-header");
      this.addCanvasToPdf(canvas, PDF, 0);
      var canvas2 = await this.createCanvasFromElement("print-footer");
      this.addCanvasToPdf(canvas2, PDF, 272);
    }
    return PDF;
  }

  async addImages(PDF: jsPDF) {
    var canvas = await this.createCanvasFromElement("print-images");
    this.addCanvasToPdf(canvas, PDF, ((PDF as any).lastAutoTable.finalY || 15) + 4);
  }

  public openPDF(): void {
    this.preparePdf((PDF) =>
      PDF.save(this.GetQuotationTitle())
    );
  }

  printQuotation() {
    this.preparePdf((PDF) => {
      PDF.autoPrint();
      window.open(
        PDF.output("bloburl"),
        "_blank",
        "toolbar=0,location=0,menubar=0"
      );
    });
  }

  emailQuotation() {
    this.preparePdf((PDF) => {
      var blobPDF = new Blob([PDF.output("blob")], { type: "application/pdf" });

      var dialog = this.dialog.open(PrintQuotationEmailDialogComponent, {
        panelClass: "print-quotation-email-dialog",
        data: {
          email: this.model?.quoteContactEmail,
          name: this.model?.quoteContactName,
          quote: "R" + this.model?.quoteId,
          purchaseOrders: this.model?.purchaseOrders,
          currentUser: this.currentUser,
          blobPDFCertificate: blobPDF,
          loadcellId: this.model?.loadcellIds[0],
        },
      });
    });
  }
}
